import { Link } from 'react-router-dom';
import { useState } from 'react';
import { companies } from '../../mocks/companies';
import TikTokVideoCard from '../../components/feature/TikTokVideoCard';
import VerticalVideoMode from '../../components/feature/VerticalVideoMode';

const tiktokLinks: Record<number, string> = {
  1: 'https://vt.tiktok.com/ZSmRRE56f/',
  2: 'https://vt.tiktok.com/ZSmR8vvAC/',
  3: 'https://vt.tiktok.com/ZSmRFpA2y/',
  4: 'https://vt.tiktok.com/ZSmRFEDe9/',
  5: 'https://vt.tiktok.com/ZSmRYYkXS/',
  7: 'https://vt.tiktok.com/ZSmRYx6rM/',
  8: 'https://vt.tiktok.com/ZSmRYqs5A/',
  9: 'https://vt.tiktok.com/ZSmRYGWL7/',
  10: 'https://vt.tiktok.com/ZSmR2dvRh/',
  16: 'https://vt.tiktok.com/ZSu9ymhtA/',
  17: 'https://vt.tiktok.com/ZSu9yVmw9/',
  18: 'https://vt.tiktok.com/ZSu9fR3sn/',
  19: 'https://vt.tiktok.com/ZSu9ffHYw/',
  20: 'https://vt.tiktok.com/ZSu9fXtKH/',
  21: 'https://vt.tiktok.com/ZSu9P8AQQ/',
  22: 'https://vt.tiktok.com/ZSu9PYjdp/',
  23: 'https://vt.tiktok.com/ZSu9PRCng/',
  24: 'https://vt.tiktok.com/ZSu95CL5J/',
  25: 'https://vt.tiktok.com/ZSu95nmpL/',
  26: 'https://vt.tiktok.com/ZSu9a641U/',
  27: 'https://vt.tiktok.com/ZSu9PGLRM/',
  28: 'https://vt.tiktok.com/ZSu9aujXP/',
  29: 'https://vt.tiktok.com/ZSuxoXeda/',
  30: 'https://vt.tiktok.com/ZSuQ1YgLM/',
};

function CompanyCard({ company, showTiktokLink = false }: { company: typeof companies[0]; showTiktokLink?: boolean }) {
  const tiktokUrl = showTiktokLink ? tiktokLinks[company.id] : undefined;

  return (
    <div className="group cursor-pointer block transition-transform duration-300 hover:-translate-y-1">
      <Link to={`/company/${company.id}`} className="block">
        <div className="relative rounded-2xl overflow-hidden mb-3 aspect-[9/16] bg-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300">
          <img
            src={company.videoThumbnail}
            alt={company.name}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/10 group-hover:from-black/10 group-hover:to-black/20 transition-all duration-300" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 flex items-center justify-center bg-white/95 rounded-full shadow-xl group-hover:scale-110 group-hover:bg-white transition-all duration-300">
              <i className="ri-play-fill text-3xl text-gray-900 ml-1"></i>
            </div>
          </div>
          <div className="absolute top-3 left-3 bg-black/75 backdrop-blur-md px-3 py-1.5 rounded-full text-xs text-white font-medium z-10 shadow-sm">
            {company.industry}
          </div>
        </div>
      </Link>
      <div className="px-1">
        <h3 className="font-bold text-sm mb-1.5 line-clamp-1 text-gray-900">
          {tiktokUrl ? (
            <a
              href={tiktokUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-700 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {company.name}
            </a>
          ) : (
            <Link to={`/company/${company.id}`} className="hover:text-gray-700 transition-colors">
              {company.name}
            </Link>
          )}
        </h3>
        <p className="text-xs text-gray-600 mb-2 line-clamp-2 leading-relaxed">{company.editorNote}</p>
        <div className="flex flex-wrap gap-1.5">
          {company.tags.slice(0, 2).map((tag, idx) => (
            <span key={idx} className="text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded-md">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [isVerticalMode, setIsVerticalMode] = useState(false);
  const [verticalIndex, setVerticalIndex] = useState(0);

  const featuredCompanies = companies.filter(c => c.id <= 10 || (c.id >= 16 && c.id <= 20) || c.id === 29 || c.id === 30);
  const liveToday = companies.filter(c => c.hasLiveToday);
  const industrySpotlight = companies.filter(c => ['飲食', '物流', '警備', '建設', '電気工事', '介護'].includes(c.industry)).slice(0, 8);
  const newCompanies = companies.filter(c => c.publishedDays <= 2).slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between" style={{ height: '100px' }}>
          <Link to="/" className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/bf28291cc5d7f0a9c8281f40e0310854/bac760dd5111dec7a1acf46dcaf57f3b.jpeg" 
              alt="縦長ジョブ" 
              className="h-16 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (isVerticalMode) {
                  setIsVerticalMode(false);
                } else {
                  setIsVerticalMode(true);
                }
              }}
              className="px-6 py-3 bg-white border-2 border-gray-900 text-gray-900 rounded-full text-sm font-medium hover:bg-gray-50 transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              {isVerticalMode ? 'TOPへ' : '縦長モード'}
            </button>
            {isVerticalMode && (
              <Link
                to={`/company/${featuredCompanies[verticalIndex]?.id}`}
                className="px-6 py-3 bg-gray-900 text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer"
              >
                企業詳細を見る →
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Vertical Video Mode */}
      {isVerticalMode && (
        <VerticalVideoMode
          companies={featuredCompanies}
          onClose={() => setIsVerticalMode(false)}
          onIndexChange={setVerticalIndex}
        />
      )}

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 leading-tight">
            縦動画で見つける、<br />新しい仕事との出会い
          </h1>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            話題の企業と、今すぐ見られる会社説明会を集めました
          </p>
        </div>
      </section>

      {/* Featured Section */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">今週の注目</h2>
            <span className="text-sm text-gray-500 font-medium">🔥 話題の企業</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {featuredCompanies.map((company) => (
              <TikTokVideoCard 
                key={company.id} 
                company={company} 
                tiktokUrl={tiktokLinks[company.id]}
                variant="normal"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Live Today Section */}
      <section className="py-12 px-4 sm:px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">今夜・今週の説明会</h2>
            <Link to="/sessions" className="text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors cursor-pointer">
              すべて見る →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {liveToday.map((company) => (
              <TikTokVideoCard 
                key={company.id} 
                company={company} 
                tiktokUrl={tiktokLinks[company.id]}
                variant="live"
                liveSchedule={company.liveSchedule}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Industry Spotlight */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">人手不足業界特集</h2>
            <span className="text-sm text-gray-500 font-medium">💼 注目の業界</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {industrySpotlight.map((company) => (
              <TikTokVideoCard 
                key={company.id} 
                company={company} 
                tiktokUrl={tiktokLinks[company.id]}
                variant="normal"
              />
            ))}
          </div>
        </div>
      </section>

      {/* New Companies */}
      <section className="py-12 px-4 sm:px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">初参加企業</h2>
            <span className="text-sm text-gray-500 font-medium">✨ 新着</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {newCompanies.map((company) => (
              <CompanyCard key={company.id} company={company} showTiktokLink={true} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-4 sm:px-6 bg-gray-900 text-white mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <img 
                src="https://static.readdy.ai/image/bf28291cc5d7f0a9c8281f40e0310854/bac760dd5111dec7a1acf46dcaf57f3b.jpeg" 
                alt="縦長ジョブ" 
                className="h-48 w-auto mb-4"
              />
              <p className="text-sm text-gray-400 leading-relaxed">
                縦動画で見つける、新しい仕事との出会い
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">サイトマップ</h4>
              <ul className="space-y-3 text-sm text-gray-400">
                <li><Link to="/" className="hover:text-white transition-colors cursor-pointer">ホーム</Link></li>
                <li><Link to="/sessions" className="hover:text-white transition-colors cursor-pointer">説明会一覧</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors cursor-pointer">利用規約</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors cursor-pointer">プライバシーポリシー</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">対象業界</h4>
              <ul className="space-y-3 text-sm text-gray-400">
                <li>飲食</li>
                <li>小売</li>
                <li>物流</li>
                <li>製造</li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            <p>&copy; 2025 縦長ジョブ. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}